import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { Table as TableV5 } from 'antd-v5';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import {
  getCustomFieldMap,
  getFormattedListViewTask,
  getSummaryViewColumns,
} from './timecardListSummaryView.helpers';
import { getIdMap, safeExtractFromObject } from '../helpers/helpers';
import Permissions from '../auth/Permissions';
import { getCustomFields, updateTimeCardFilters } from './state/timecards.actions';

export default function TimeCardsSummaryView({
  tasks = [],
  isApprovals,
  onSummaryTaskModify,
  onSummaryRowClick,
  onRowSelectChange,
  onTableChange,
  onFormClick,
  selectedSummaryRowKeys = [],
  entryDataMap,
  height,
  hoursBreakdown,
  isLoading,
  onCardColumnClick,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const hasApproveAllPermissions = Permissions.has('ENABLE_APPROVE_ALL');

  const unions = useSelector((state) => state.unions.unions);
  const unionClasses = useSelector((state) => state.unions.classes);
  const unionLocals = useSelector((state) => state.unions.locals);
  const customFields = useSelector((state) => state.timecards.customFields);
  const users = useSelector((state) => state.users.users);
  const settings = useSelector((state) => safeExtractFromObject(state, 'settings.company.settings'));

  const enableHourBasedTracking = useSelector((state) => {
    const {
      settings: {
        company: {
          settings: {
            enableHourBasedTracking: enableHourBasedTrackingSetting = false,
          } = {},
        } = {},
      } = {},
    } = state;
    return enableHourBasedTrackingSetting;
  });
  const phases = useSelector((state) => state.costcodes.phases);
  const costcodeIdMap = useSelector((state) => state.costcodes.costcodeIdMap);

  const [selectedTimeCardSummaryColumns, setSelectedTimeCardSummaryColumns] = useState();
  const [selectedCustomFieldColumns, setSelectedCustomFieldColumns] = useState();

  const userMap = useMemo(() => getIdMap(users), [users]);
  const unionMap = useMemo(() => getIdMap(unions), [unions]);
  const unionClassMap = useMemo(() => getIdMap(unionClasses), [unionClasses]);
  const unionLocalMap = useMemo(() => getIdMap(unionLocals), [unionLocals]);

  const phaseMap = useMemo(() => phases.reduce((acc, phase) => {
    if (!acc[phase.id]) acc[phase.id] = [];
    acc[phase.id].push(phase);
    return acc;
  }, {}), [phases]);

  const customFieldMap = useMemo(() => getCustomFieldMap(customFields), [customFields]);

  const filteredTasks = useMemo(() => {
    const seen = new Set();
    return tasks.filter((task) => !task.id || (!seen.has(task.id) && seen.add(task.id)));
  }, [tasks]);

  useEffect(() => {
    const onStorageUpdated = (e) => {
      if (!e || e.detail === 'timeCardSummaryColumns') {
        const rawTimeCardSummaryColumns = window.localStorage.getItem('timeCardSummaryColumns');
        if (rawTimeCardSummaryColumns) {
          setSelectedTimeCardSummaryColumns(JSON.parse(rawTimeCardSummaryColumns));
        }
      }

      if (!e || e.detail === 'timeCardCustomFieldColumns') {
        const rawCustomFieldColumns = window.localStorage.getItem('timeCardCustomFieldColumns');
        if (rawCustomFieldColumns) {
          setSelectedCustomFieldColumns(JSON.parse(rawCustomFieldColumns));
        }
      }
    };

    onStorageUpdated();
    window.addEventListener('localStorageSetItem', onStorageUpdated);

    return () => {
      window.removeEventListener('localStorageSetItem', onStorageUpdated);
    };
  }, []);

  const formattedTasks = useMemo(() => (
    filteredTasks
      .map((task) => getFormattedListViewTask({
        task,
        costCodeMap: costcodeIdMap,
        phaseMap,
        enableHourBasedTracking,
        unionClassMap,
        unionLocalMap,
        unionMap,
        entryDataMap,
        userMap,
        hoursBreakdown,
      }))
  ), [
    filteredTasks,
    costcodeIdMap,
    phaseMap,
    unionMap,
    unionClassMap,
    unionLocalMap,
    enableHourBasedTracking,
    userMap,
    entryDataMap,
    hoursBreakdown,
  ]);

  const divisionIdsInTasks = useMemo(() => (
    new Set(formattedTasks.map((task) => task.divisionId))
  ), [formattedTasks]);

  const columns = useMemo(() => getSummaryViewColumns({
    t,
    isApprovals,
    onSummaryTaskModify,
    onFormClick,
    customFieldMap,
    divisionIds: divisionIdsInTasks,
    selectedTimeCardSummaryColumns,
    selectedCustomFieldColumns,
    onCardColumnClick,
    enableNightShiftPremium: settings?.enableNightShiftPremium,
  }), [
    t,
    isApprovals,
    onSummaryTaskModify,
    onFormClick,
    customFieldMap,
    divisionIdsInTasks,
    selectedTimeCardSummaryColumns,
    selectedCustomFieldColumns,
    onCardColumnClick,
    settings?.enableNightShiftPremium,
  ]);

  const rowSelectionProps = useMemo(() => {
    if (!isApprovals || !hasApproveAllPermissions) return {};

    return {
      rowSelection: {
        selectedRowKeys: selectedSummaryRowKeys,
        hideSelectAll: !hasApproveAllPermissions,
        onChange: onRowSelectChange,
        columnWidth: 50,
        fixed: true,
        getCheckboxProps: () => ({
          className: settings?.uxRedesignPt1 ? 'ontraccr-checkbox' : '',
        }),
      },
    };
  }, [
    selectedSummaryRowKeys,
    hasApproveAllPermissions,
    isApprovals,
    onRowSelectChange,
    settings,
  ]);

  useEffect(() => {
    dispatch(getCustomFields());
  }, []);

  useEffect(() => (
    () => {
      dispatch(updateTimeCardFilters(null));
    }
  ), []);

  const onHeaderRow = useCallback(() => {
    if (!settings?.uxRedesignPt1) return 'timecard-entry-header-row';
    return {
      className: 'timecard-entry-header-row ontraccr-table-header',
    };
  }, [settings]);

  return (
    <TableV5
      virtual
      scroll={{ x: 'max-content', y: height - 250 }}
      columns={columns}
      dataSource={formattedTasks}
      pagination={false}
      size="small"
      rowClassName={() => 'timecard-entry-row'}
      rowKey="id"
      bordered
      onRow={(rowRecord) => ({
        onClick: async () => onSummaryRowClick(rowRecord.id),
        isPending: rowRecord.isPending,
        formUserName: rowRecord.formUserName,
        formCreatedAt: rowRecord.formCreatedAt,
        formName: rowRecord.formName,
        formNumber: rowRecord.formNumber,
      })}
      onHeaderRow={onHeaderRow}
      onChange={onTableChange}
      loading={isLoading}
      {...rowSelectionProps} // eslint-disable-line react/jsx-props-no-spreading
    />
  );
}

/* eslint-disable react/forbid-prop-types */
TimeCardsSummaryView.propTypes = {
  tasks: PropTypes.array,
  isApprovals: PropTypes.bool.isRequired,
  onSummaryTaskModify: PropTypes.func.isRequired,
  onSummaryRowClick: PropTypes.func.isRequired,
  onRowSelectChange: PropTypes.func.isRequired,
  selectedSummaryRowKeys: PropTypes.array,
  entryDataMap: PropTypes.object.isRequired,
  onTableChange: PropTypes.func.isRequired,
  onFormClick: PropTypes.func.isRequired,
  height: PropTypes.number.isRequired,
  hoursBreakdown: PropTypes.object,
  isLoading: PropTypes.bool,
  onCardColumnClick: PropTypes.func,
};

TimeCardsSummaryView.defaultProps = {
  tasks: [],
  selectedSummaryRowKeys: [],
  hoursBreakdown: {},
  isLoading: false,
  onCardColumnClick: () => {},
};
